import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { buildShotUrl, getAgentSite, siteUrl, startAgentChange, type AgentImage, type AgentSite } from '../../api/agent';
import { ApiError } from '../../api/client';
import { Button, Screen } from '../../ui';
import Composer from './Composer';
import ShareBar from './ShareBar';
import './studio.css';

/** /studio/sites/:siteId — one of your sites: where it lives, share it, change it. */
export default function StudioSite() {
  const { siteId = '' } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState<AgentSite | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    let live = true;
    getAgentSite(siteId).then(
      (s) => live && setSite(s),
      () => live && setProblem("We couldn't find that site."),
    );
    return () => {
      live = false;
    };
  }, [siteId]);

  async function change(message: string, images: AgentImage[]) {
    setChanging(true);
    try {
      const { buildId } = await startAgentChange(siteId, message, images);
      navigate(`/studio/builds/${buildId}`);
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : 'That did not start. Try again in a moment.');
      setChanging(false);
    }
  }

  if (!site) {
    return <Screen title="Your site">{problem ? <p className="studio-problem">{problem}</p> : <div className="studio-spinner" aria-label="Loading" />}</Screen>;
  }

  if (!site.slug || !site.currentBuildId) {
    return (
      <Screen title={site.title ?? 'Building…'}>
        <p className="studio-muted">This one isn't live yet.</p>
        {site.pendingBuildId && <Button title="See how it's going" onPress={() => navigate(`/studio/builds/${site.pendingBuildId}`)} />}
      </Screen>
    );
  }

  const url = siteUrl(site.slug);
  return (
    <Screen title={site.title ?? 'Your site'}>
      <div className="studio-result">
        <a className="studio-phone pressable" href={url} target="_blank" rel="noreferrer" aria-label={`Open ${site.title ?? 'your site'}`}>
          <img src={buildShotUrl(site.slug, site.currentBuildId)} alt={`${site.title ?? 'Your site'} on a phone`} />
        </a>
        {site.description && <p className="studio-summary">{site.description}</p>}
        <ShareBar url={url} title={site.title ?? 'My site'} />
      </div>
      {site.pendingBuildId ? (
        <Button title="A change is being made — see it" variant="secondary" onPress={() => navigate(`/studio/builds/${site.pendingBuildId}`)} />
      ) : (
        <section className="studio-section" aria-labelledby="studio-change">
          <h2 id="studio-change" className="studio-h2">Change something</h2>
          <Composer placeholder="What should be different?" submitLabel="Make the change" busy={changing} onSubmit={change} />
        </section>
      )}
      {problem && <p className="studio-problem" role="alert">{problem}</p>}
    </Screen>
  );
}
